/**
 * Phase C — Primavera P6 tab on the Project Detail page.
 * Export the schedule to a valid .xer (with a pre-download summary), import a
 * P6-edited XER to update progress (with a reconciliation report), and show the
 * L3/L4 compliance readiness score tier-1 clients care about.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Download, Upload, FileDown, CheckCircle2, XCircle, AlertTriangle, Info, Loader2, RefreshCw, ShieldCheck, GitBranch, Zap, ShieldAlert, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiGet, apiPost } from '../../lib/api';
import { formatDateTime as fmtDate } from '../../lib/format';

function ReadinessBar({ label, pct }) {
  const color = pct >= 90 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div data-testid={`readiness-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-xs font-display font-semibold mb-1">
        <span>{label} ready</span><span className="tabular-nums">{pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const CHECK_ICON = {
  pass: <CheckCircle2 size={14} className="text-emerald-600" />,
  fail: <XCircle size={14} className="text-rose-600" />,
  info: <Info size={14} className="text-sky-600" />,
};

export function PrimaveraTab({ project }) {
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const [log, setLog] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [cpm, setCpm] = useState(null);
  const [cpmLoading, setCpmLoading] = useState(false);
  const [cpmError, setCpmError] = useState(null);
  const [cpmAt, setCpmAt] = useState(null);
  const [risks, setRisks] = useState([]);
  const fileRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const l = await apiGet(`/primavera/sync-log?project_id=${project.id}`);
      setLog(l.items || []);
    } catch { /* noop */ }
    try {
      setCompliance(await apiGet(`/primavera/compliance-report/${project.id}`));
    } catch { setCompliance(null); }
    try {
      const r = await apiGet(`/primavera/schedule-risks?project_id=${project.id}&status=open`);
      setRisks(r.items || []);
    } catch { setRisks([]); }
  }, [project.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const lastExport = log.find((x) => x.direction === 'export');
  const lastImport = log.find((x) => x.direction === 'import');

  const openPreview = async () => {
    setLoadingPrev(true);
    try {
      setPreview(await apiGet(`/primavera/export/${project.id}/preview`));
      setShowPreview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build export preview');
    } finally { setLoadingPrev(false); }
  };

  const downloadXer = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/primavera/export/${project.id}.xer`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `${project.code || 'project'}.xer`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success('XER exported');
      setShowPreview(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally { setDownloading(false); }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xer')) { toast.error('Please select a .xer file'); return; }
    setImporting(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/primavera/import/${project.id}/progress`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(res.data);
      toast.success(`Imported · ${res.data.matched} matched, ${res.data.updated.length} updated`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const actOnRisk = async (id, action) => {
    try {
      await apiPost(`/primavera/schedule-risks/${id}/${action}`);
      toast.success(action === 'acknowledge' ? 'Alert acknowledged' : 'Alert resolved');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const analyseCpm = async () => {
    setCpmLoading(true);
    setCpmError(null);
    try {
      const res = await apiGet(`/primavera/cpm/${project.id}?refresh=true`);
      setCpm(res);
      setCpmAt(new Date());
    } catch (e) {
      const detail = e?.response?.data?.detail || 'CPM analysis failed';
      setCpmError(detail);
      setCpm(null);
      toast.error(detail);
    } finally { setCpmLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="primavera-tab">
      {/* Action cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-sm p-5 bg-white">
          <div className="flex items-center gap-2 text-[#3b4872] mb-1"><Download size={16} /><span className="font-display font-bold text-sm uppercase tracking-wider">Export to Primavera</span></div>
          <p className="text-xs text-slate-500 mb-3">Generate a P6-readable <span className="font-mono">.xer</span> of this project's WBS &amp; activities.</p>
          <button onClick={openPreview} disabled={loadingPrev} data-testid="primavera-export-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2d3856] disabled:opacity-50">
            {loadingPrev ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Export (.xer)
          </button>
          <p className="text-[11px] text-slate-400 mt-3" data-testid="primavera-last-export">Last exported: {fmtDate(lastExport?.performed_at)}{lastExport ? ` · by ${lastExport.performed_by}` : ''}</p>
        </div>

        <div className="border border-slate-200 rounded-sm p-5 bg-white">
          <div className="flex items-center gap-2 text-[#3b4872] mb-1"><Upload size={16} /><span className="font-display font-bold text-sm uppercase tracking-wider">Import Progress</span></div>
          <p className="text-xs text-slate-500 mb-3">Upload a P6-updated <span className="font-mono">.xer</span> to sync actual dates &amp; completion back.</p>
          <input ref={fileRef} type="file" accept=".xer" hidden onChange={onImportFile} data-testid="primavera-import-input" />
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="primavera-import-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#3b4872] text-[#3b4872] text-sm font-display font-semibold rounded-sm hover:bg-slate-50 disabled:opacity-50">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import .xer
          </button>
          <p className="text-[11px] text-slate-400 mt-3" data-testid="primavera-last-import">Last imported: {fmtDate(lastImport?.performed_at)}{lastImport ? ` · by ${lastImport.performed_by}` : ''}</p>
        </div>
      </div>

      {/* Compliance readiness */}
      {compliance && (
        <div className="border border-slate-200 rounded-sm p-5 bg-white" data-testid="primavera-compliance">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-[#3b4872]"><ShieldCheck size={16} /><span className="font-display font-bold text-sm uppercase tracking-wider">L3/L4 Compliance Readiness</span></div>
            <button onClick={refresh} className="text-slate-400 hover:text-[#3b4872]"><RefreshCw size={14} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <ReadinessBar label="L3" pct={compliance.l3_ready_pct} />
            <ReadinessBar label="L4" pct={compliance.l4_ready_pct} />
          </div>
          <div className="space-y-1.5">
            {compliance.checks.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-xs" data-testid={`compliance-check-${c.id}`}>
                {CHECK_ICON[c.status]}
                <span className="font-display font-semibold text-slate-700">{c.label}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-display font-bold">{c.level}</span>
                <span className="text-slate-400 ml-auto">{c.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Critical Path (native CPM) */}
      <div className="border border-slate-200 rounded-sm p-5 bg-white" data-testid="primavera-cpm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[#3b4872]"><GitBranch size={16} /><span className="font-display font-bold text-sm uppercase tracking-wider">Critical Path Analysis</span></div>
          <button onClick={analyseCpm} disabled={cpmLoading} data-testid="cpm-analyse-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2d3856] disabled:opacity-50">
            {cpmLoading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Analyse Critical Path
          </button>
        </div>

        {cpmError && (
          <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2" data-testid="cpm-error">
            <AlertTriangle size={13} /> {cpmError}
          </div>
        )}

        {cpm && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="cpm-summary">
              {[['Critical path', `${cpm.summary.critical_path_duration_days} days`],
                ['On critical path', `${cpm.summary.critical_tasks_count} activities`],
                ['Near-critical (≤3d)', `${cpm.summary.near_critical_tasks_count} activities`],
                ['Project float', `${cpm.summary.project_float_days} days`]].map(([l, v]) => (
                <div key={l} className="border border-slate-100 rounded-sm py-2 px-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold">{l}</div>
                  <div className="text-lg font-display font-bold text-slate-900">{v}</div>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="cpm-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Activity</th>
                    <th className="px-2 py-1.5 text-right">Dur</th>
                    <th className="px-2 py-1.5 text-right">ES</th>
                    <th className="px-2 py-1.5 text-right">EF</th>
                    <th className="px-2 py-1.5 text-right">Total Float</th>
                    <th className="px-2 py-1.5 text-center">Critical</th>
                  </tr>
                </thead>
                <tbody>
                  {cpm.tasks.map((t) => (
                    <tr key={t.task_id}
                      className={`border-t border-slate-100 ${t.is_critical ? 'bg-rose-50/60' : t.is_near_critical ? 'bg-amber-50/60' : ''}`}
                      data-testid={`cpm-row-${t.code}`}>
                      <td className="px-2 py-1.5 font-display font-semibold text-slate-800">{t.task_name}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{t.duration_days}d</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{t.ES}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{t.EF}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${t.is_critical ? 'text-rose-700' : t.is_near_critical ? 'text-amber-700' : 'text-slate-600'}`}>{t.TF}d</td>
                      <td className="px-2 py-1.5 text-center">
                        {t.is_critical
                          ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-600 text-white font-display font-bold uppercase">Critical</span>
                          : t.is_near_critical
                            ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-display font-bold uppercase">Near</span>
                            : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <p className="text-[11px] text-slate-400 mt-3" data-testid="cpm-last-analysed">
          Last analysed: {cpmAt ? fmtDate(cpmAt) : '—'}
        </p>
      </div>

      {/* Schedule Risk Log (Phase D) */}
      <div className="border border-slate-200 rounded-sm p-5 bg-white" data-testid="schedule-risk-log">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[#3b4872]"><ShieldAlert size={16} /><span className="font-display font-bold text-sm uppercase tracking-wider">Schedule Risk Log</span></div>
          <span className="text-[11px] text-slate-400">Auto-raised when daily progress drops an activity's float ≤ 3 days</span>
        </div>
        {risks.length === 0 ? (
          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="schedule-risk-empty">
            <CheckCircle2 size={13} /> No open schedule risks. Every activity currently has &gt;3 days of float.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="schedule-risk-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold">
                <tr>
                  <th className="px-2 py-1.5 text-left">Activity</th>
                  <th className="px-2 py-1.5 text-center">Risk</th>
                  <th className="px-2 py-1.5 text-right">Total Float</th>
                  <th className="px-2 py-1.5 text-right">Late Finish</th>
                  <th className="px-2 py-1.5 text-center">Status</th>
                  <th className="px-2 py-1.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {risks.map((r) => (
                  <tr key={r.id} className={`border-t border-slate-100 ${r.alert_type === 'critical' ? 'bg-rose-50/60' : 'bg-amber-50/60'}`} data-testid={`schedule-risk-row-${r.id}`}>
                    <td className="px-2 py-1.5 font-display font-semibold text-slate-800">{r.task_name}</td>
                    <td className="px-2 py-1.5 text-center">
                      {r.alert_type === 'critical'
                        ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-600 text-white font-display font-bold uppercase">Critical</span>
                        : <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-display font-bold uppercase">Near</span>}
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${r.alert_type === 'critical' ? 'text-rose-700' : 'text-amber-700'}`}>{r.total_float_days}d</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{r.lf || '—'}</td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-display font-bold uppercase ${r.status === 'new' ? 'bg-sky-100 text-sky-700' : 'bg-slate-200 text-slate-600'}`}>{r.status}</span>
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {r.status === 'new' && (
                        <button onClick={() => actOnRisk(r.id, 'acknowledge')} data-testid={`risk-ack-${r.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm border border-slate-300 hover:bg-slate-50 mr-1"><Check size={11} /> Ack</button>
                      )}
                      <button onClick={() => actOnRisk(r.id, 'resolve')} data-testid={`risk-resolve-${r.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50"><CheckCircle2 size={11} /> Resolve</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reconciliation report */}
      {report && (
        <div className="border border-slate-200 rounded-sm p-5 bg-white" data-testid="primavera-recon-report">
          <div className="font-display font-bold text-sm uppercase tracking-wider text-[#3b4872] mb-3">Import Reconciliation</div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {[['Matched', report.matched, 'text-slate-900'], ['Updated', report.updated.length, 'text-emerald-700'],
              ['Unmatched', report.unmatched.length, 'text-amber-700'], ['Skipped (done)', report.skipped_complete.length, 'text-slate-500']].map(([l, v, c]) => (
              <div key={l} className="text-center border border-slate-100 rounded-sm py-2">
                <div className={`text-2xl font-display font-bold tabular-nums ${c}`}>{v}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold">{l}</div>
              </div>
            ))}
          </div>
          {report.updated.length > 0 && (
            <table className="w-full text-xs mb-3"><thead className="bg-slate-50"><tr><th className="px-2 py-1.5 text-left">Activity</th><th className="px-2 py-1.5 text-right">Progress</th><th className="px-2 py-1.5 text-right">Actual Start</th><th className="px-2 py-1.5 text-right">Actual End</th></tr></thead>
              <tbody>{report.updated.map((u, i) => (
                <tr key={i} className="border-t border-slate-100"><td className="px-2 py-1.5">{u.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{u.progress != null ? `${u.progress}%` : '—'}</td>
                  <td className="px-2 py-1.5 text-right">{u.actual_start || '—'}</td>
                  <td className="px-2 py-1.5 text-right">{u.actual_end || '—'}</td></tr>))}</tbody></table>
          )}
          {report.unmatched.length > 0 && (
            <div className="text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>Unmatched (left untouched): {report.unmatched.map((u) => u.task_name || u.task_code).join(', ')}</span></div>
          )}
        </div>
      )}

      {/* Preview modal */}
      {showPreview && preview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-sm max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="primavera-preview-modal">
            <div className="font-display font-bold text-lg mb-1">Export to Primavera</div>
            <p className="text-sm text-slate-600 mb-4">
              This will export <b>{preview.activity_count} activities</b> across <b>{preview.wbs_node_count} WBS nodes</b>
              {preview.relationship_count ? <> with <b>{preview.relationship_count} relationships</b></> : null}.
            </p>
            <div className="space-y-1.5 text-xs text-slate-600 mb-5">
              <div className="flex justify-between"><span>Date range</span><span className="font-mono">{preview.date_range.start || '—'} → {preview.date_range.end || '—'}</span></div>
              <div className="flex justify-between"><span>Calendar</span><span>{preview.calendar}</span></div>
              <div className="flex justify-between"><span>Baseline frozen</span><span>{preview.baseline_frozen ? 'Yes' : 'No'}</span></div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowPreview(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm font-display font-semibold">Cancel</button>
              <button onClick={downloadXer} disabled={downloading} data-testid="primavera-download-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2d3856] disabled:opacity-50">
                {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download .xer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrimaveraTab;
